import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from './Icons';

// Render a single card slot with holographic effect - moved outside component
const CardSlot = ({ card, slotIndex, onCardClick, getRarityStyle }) => (
  <div 
    className={`card-slot aspect-[63/88] rounded-sm overflow-hidden cursor-pointer transition-all duration-300 relative group ${
      card ? 'hover:scale-105 hover:z-20' : ''
    }`}
    onClick={() => {
      if (card && onCardClick) {
        onCardClick(card);
      }
    }}
    data-testid={card ? `card-slot-${card.id}` : `empty-slot-${slotIndex}`}
  >
    {/* Slot pocket effect */}
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20 pointer-events-none z-10" />
    <div className="absolute inset-[2px] rounded-sm bg-gray-950/90 border border-gray-700/50 pointer-events-none" />
    
    {card ? (
      <div className="absolute inset-[3px] rounded-sm overflow-hidden pointer-events-none">
        {/* Card image */}
        <img 
          src={card.image} 
          alt={card.name}
          className="w-full h-full object-cover pointer-events-none"
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = `https://placehold.co/200x280/1a1a2e/ffffff?text=${encodeURIComponent(card.name?.substring(0, 8) || 'Card')}`;
          }}
        />
        
        {/* Holographic overlay for rare cards */}
        {card.rarity && card.rarity !== 'Common' && (
          <div className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-300 pointer-events-none">
            <div className={`w-full h-full bg-gradient-to-br ${getRarityStyle(card.rarity)} animate-holo`} />
          </div>
        )}
        
        {/* Card info overlay on hover */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/95 to-transparent p-1.5 sm:p-2 translate-y-full group-hover:translate-y-0 transition-transform duration-200 pointer-events-none">
          <p className="text-[7px] sm:text-[9px] text-white font-bold truncate leading-tight">{card.name}</p>
          {card.rarity && (
            <span className={`inline-block mt-0.5 px-1 py-0.5 rounded text-[6px] sm:text-[7px] font-bold bg-gradient-to-r ${getRarityStyle(card.rarity)} text-black`}>
              {card.rarity}
            </span>
          )}
          {card.price && (
            <p className="text-[8px] sm:text-[10px] text-yellow-400 font-black mt-0.5">${card.price}</p>
          )}
        </div>
        
        {/* Stock indicator */}
        {card.stock !== undefined && (
          <div className="absolute top-1 right-1 px-1 py-0.5 bg-black/80 rounded text-[6px] sm:text-[7px] text-white font-bold pointer-events-none">
            x{card.stock}
          </div>
        )}
      </div>
    ) : (
      /* Empty slot placeholder */
      <div className="absolute inset-[3px] flex items-center justify-center">
        <div className="w-3/4 h-3/4 border border-dashed border-gray-800 rounded-sm opacity-40" />
      </div>
    )}
  </div>
);

// Render a page with card grid - moved outside component
const PageGrid = ({ pageCards, side, colors, gridCols, gridRows, currentPage, onCardClick, getRarityStyle }) => (
  <div 
    className={`page-content w-full h-full ${colors.inner} rounded-sm p-1.5 sm:p-2 md:p-3 relative overflow-hidden`}
    style={{ 
      boxShadow: side === 'left' 
        ? 'inset -4px 0 12px rgba(0,0,0,0.4), inset 0 0 30px rgba(0,0,0,0.2)' 
        : 'inset 4px 0 12px rgba(0,0,0,0.4), inset 0 0 30px rgba(0,0,0,0.2)' 
    }}
  >
    {/* Page texture */}
    <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
    }} />
    
    {/* Page number watermark */}
    <div className={`absolute ${side === 'left' ? 'bottom-1 left-2' : 'bottom-1 right-2'} text-[10px] text-gray-700 font-mono`}>
      {side === 'left' ? currentPage * 2 + 1 : currentPage * 2 + 2}
    </div>
    
    {/* Card grid */}
    <div 
      className="grid gap-1 sm:gap-1.5 md:gap-2 h-full relative z-10"
      style={{ 
        gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
        gridTemplateRows: `repeat(${gridRows}, 1fr)`
      }}
    >
      {Array.from({ length: gridCols * gridRows }).map((_, idx) => (
        <CardSlot 
          key={`${side}-${idx}`} 
          card={pageCards[idx]} 
          slotIndex={idx} 
          onCardClick={onCardClick}
          getRarityStyle={getRarityStyle}
        />
      ))}
    </div>
  </div>
);

/**
 * CardBinder Component - Simulates a physical card binder with realistic page-flip animation
 * @param {Array} cards - Array of card objects with id, name, image, price, rarity
 * @param {number} gridCols - Number of columns (default 4)
 * @param {number} gridRows - Number of rows (default 4)
 * @param {string} binderColor - Color of the binder (cyan, purple, red, blue, black, gold)
 * @param {function} onCardClick - Callback when a card is clicked
 */
const CardBinder = ({ 
  cards = [], 
  gridCols = 4, 
  gridRows = 4, 
  binderColor = 'cyan',
  onCardClick 
}) => {
  const cardsPerPage = gridCols * gridRows * 2; // Both sides of the spread
  const cardsPerSide = gridCols * gridRows;
  const totalPages = Math.max(1, Math.ceil(cards.length / cardsPerPage));
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState(null);
  const [touchStart, setTouchStart] = useState(null);

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages - 1 && !isFlipping) {
      setFlipDirection('next');
      setIsFlipping(true);
      setTimeout(() => {
        setCurrentPage(prev => prev + 1);
        setTimeout(() => {
          setIsFlipping(false);
          setFlipDirection(null);
        }, 50);
      }, 350);
    }
  }, [currentPage, totalPages, isFlipping]);

  const goToPrevPage = useCallback(() => {
    if (currentPage > 0 && !isFlipping) {
      setFlipDirection('prev');
      setIsFlipping(true);
      setTimeout(() => {
        setCurrentPage(prev => prev - 1);
        setTimeout(() => {
          setIsFlipping(false);
          setFlipDirection(null);
        }, 50);
      }, 350);
    }
  }, [currentPage, isFlipping]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') goToNextPage();
      if (e.key === 'ArrowLeft') goToPrevPage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNextPage, goToPrevPage]);

  // Touch/swipe handlers
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goToNextPage();
      else goToPrevPage();
    }
    setTouchStart(null);
  };

  // Get cards for current spread (left and right pages)
  const startIndex = currentPage * cardsPerPage;
  const leftPageCards = cards.slice(startIndex, startIndex + cardsPerSide);
  const rightPageCards = cards.slice(startIndex + cardsPerSide, startIndex + cardsPerPage);

  // Binder color styles - expanded palette
  const colorStyles = {
    cyan: { 
      outer: 'from-cyan-500 via-cyan-600 to-teal-700', 
      inner: 'bg-gradient-to-br from-gray-900 to-gray-800',
      spine: 'bg-gradient-to-b from-cyan-400 to-cyan-700',
      ring: 'bg-gradient-to-r from-gray-400 to-gray-600',
      accent: 'cyan'
    },
    purple: { 
      outer: 'from-purple-500 via-purple-600 to-indigo-700', 
      inner: 'bg-gradient-to-br from-gray-900 to-gray-800',
      spine: 'bg-gradient-to-b from-purple-400 to-purple-700',
      ring: 'bg-gradient-to-r from-gray-400 to-gray-600',
      accent: 'purple'
    },
    red: { 
      outer: 'from-red-500 via-red-600 to-rose-700', 
      inner: 'bg-gradient-to-br from-gray-900 to-gray-800',
      spine: 'bg-gradient-to-b from-red-400 to-red-700',
      ring: 'bg-gradient-to-r from-gray-400 to-gray-600',
      accent: 'red'
    },
    blue: { 
      outer: 'from-blue-500 via-blue-600 to-indigo-700', 
      inner: 'bg-gradient-to-br from-gray-900 to-gray-800',
      spine: 'bg-gradient-to-b from-blue-400 to-blue-700',
      ring: 'bg-gradient-to-r from-gray-400 to-gray-600',
      accent: 'blue'
    },
    black: { 
      outer: 'from-gray-800 via-gray-900 to-black', 
      inner: 'bg-gradient-to-br from-gray-950 to-black',
      spine: 'bg-gradient-to-b from-gray-700 to-gray-900',
      ring: 'bg-gradient-to-r from-gray-500 to-gray-700',
      accent: 'gray'
    },
    gold: { 
      outer: 'from-yellow-500 via-amber-600 to-orange-700', 
      inner: 'bg-gradient-to-br from-gray-900 to-gray-800',
      spine: 'bg-gradient-to-b from-yellow-400 to-amber-700',
      ring: 'bg-gradient-to-r from-yellow-300 to-amber-500',
      accent: 'yellow'
    }
  };

  const colors = colorStyles[binderColor] || colorStyles.cyan;

  // Get rarity color class
  const getRarityStyle = (rarity) => {
    const styles = {
      'Secret Rare': 'from-yellow-400 via-amber-300 to-yellow-500 animate-shimmer',
      'Ultra Rare': 'from-purple-400 via-pink-400 to-purple-500',
      'Super Rare': 'from-blue-400 via-cyan-400 to-blue-500',
      'Rare': 'from-green-400 via-emerald-400 to-green-500',
      'Common': 'from-gray-400 to-gray-500'
    };
    return styles[rarity] || styles['Common'];
  };

  return (
    <div className="w-full max-w-6xl mx-auto select-none" data-testid="card-binder">
      {/* Binder Container */}
      <div 
        className="relative px-6 sm:px-8 md:px-12"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Outer Binder Cover with leather texture */}
        <div className={`binder-cover relative bg-gradient-to-br ${colors.outer} rounded-xl p-1.5 sm:p-2 md:p-3 shadow-2xl`}>
          {/* Leather texture overlay */}
          <div className="absolute inset-0 rounded-xl opacity-30 mix-blend-overlay" 
            style={{ 
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
            }}
          />
          
          {/* Embossed border effect */}
          <div className="absolute inset-2 rounded-lg border border-white/10" />
          
          {/* Inner Binder Area */}
          <div 
            className="binder-inner relative flex bg-gray-950 rounded-lg overflow-hidden"
            style={{ minHeight: '420px', perspective: '2000px' }}
          >
            {/* Left Page */}
            <div 
              className={`page-left w-1/2 relative ${
                isFlipping && flipDirection === 'prev' ? 'z-30' : 'z-10'
              }`}
              style={{ transformStyle: 'preserve-3d' }}
            >
              <div className={`page-wrapper ${isFlipping && flipDirection === 'prev' ? 'flip-prev' : ''}`}>
                <PageGrid 
                  pageCards={leftPageCards} 
                  side="left" 
                  colors={colors}
                  gridCols={gridCols}
                  gridRows={gridRows}
                  currentPage={currentPage}
                  onCardClick={onCardClick}
                  getRarityStyle={getRarityStyle}
                />
              </div>
            </div>
            
            {/* Center Spine with rings */}
            <div className={`binder-spine w-3 sm:w-4 md:w-5 ${colors.spine} relative z-20 flex flex-col justify-evenly py-4 sm:py-6`}
              style={{ boxShadow: '0 0 15px rgba(0,0,0,0.5)' }}
            >
              {/* Binder Rings */}
              {[1, 2, 3, 4].map((ring) => (
                <div key={ring} className="ring-container relative flex justify-center">
                  <div className={`w-4 sm:w-5 md:w-6 h-4 sm:h-5 md:h-6 ${colors.ring} rounded-full shadow-lg relative overflow-hidden`}>
                    <div className="absolute inset-0.5 bg-gray-300 rounded-full" />
                    <div className="absolute inset-1 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full" />
                    <div className="absolute top-1 left-1 w-1 h-1 bg-white/50 rounded-full" />
                  </div>
                </div>
              ))}
            </div>
            
            {/* Right Page */}
            <div 
              className={`page-right w-1/2 relative ${
                isFlipping && flipDirection === 'next' ? 'z-30' : 'z-10'
              }`}
              style={{ transformStyle: 'preserve-3d' }}
            >
              <div className={`page-wrapper ${isFlipping && flipDirection === 'next' ? 'flip-next' : ''}`}>
                <PageGrid 
                  pageCards={rightPageCards} 
                  side="right"
                  colors={colors}
                  gridCols={gridCols}
                  gridRows={gridRows}
                  currentPage={currentPage}
                  onCardClick={onCardClick}
                  getRarityStyle={getRarityStyle}
                />
              </div>
            </div>
            
            {/* Page flip shadow overlay */}
            {isFlipping && (
              <div className={`absolute inset-0 pointer-events-none z-40 ${
                flipDirection === 'next' 
                  ? 'bg-gradient-to-l from-black/30 via-transparent to-transparent' 
                  : 'bg-gradient-to-r from-black/30 via-transparent to-transparent'
              } animate-fade-shadow`} />
            )}
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={goToPrevPage}
          disabled={currentPage === 0 || isFlipping}
          className={`nav-btn absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-all duration-200 ${
            currentPage === 0 
              ? 'bg-gray-900/80 text-gray-700 cursor-not-allowed border border-gray-800' 
              : 'bg-gradient-to-r from-pink-500 via-red-500 to-orange-500 text-white hover:scale-110 shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50'
          }`}
          data-testid="binder-prev"
          aria-label="Página anterior"
        >
          <ChevronLeft size={24} />
        </button>

        <button
          onClick={goToNextPage}
          disabled={currentPage >= totalPages - 1 || isFlipping}
          className={`nav-btn absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-all duration-200 ${
            currentPage >= totalPages - 1 
              ? 'bg-gray-900/80 text-gray-700 cursor-not-allowed border border-gray-800' 
              : 'bg-gradient-to-r from-pink-500 via-red-500 to-orange-500 text-white hover:scale-110 shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50'
          }`}
          data-testid="binder-next"
          aria-label="Página siguiente"
        >
          <ChevronRight size={24} />
        </button>
      </div>

      {/* Page Indicator */}
      <div className="flex flex-col items-center gap-3 mt-6">
        {/* Dot indicators */}
        <div className="flex items-center gap-1.5">
          {Array.from({ length: Math.min(totalPages, 10) }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => !isFlipping && setCurrentPage(idx)}
              className={`transition-all duration-300 rounded-full ${
                idx === currentPage 
                  ? 'w-6 h-2 bg-gradient-to-r from-yellow-400 to-amber-500' 
                  : 'w-2 h-2 bg-gray-700 hover:bg-gray-500'
              }`}
              data-testid={`page-dot-${idx}`}
            />
          ))}
          {totalPages > 10 && (
            <span className="text-gray-500 text-xs ml-1">+{totalPages - 10}</span>
          )}
        </div>
        
        {/* Page info */}
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-400">
            Página <span className="text-yellow-400 font-bold">{currentPage + 1}</span> de <span className="text-white font-bold">{totalPages}</span>
          </span>
          <span className="text-gray-600">|</span>
          <span className="text-gray-500">
            {cards.length} cartas totales
          </span>
        </div>
        
        {/* Navigation hint */}
        <p className="text-gray-600 text-xs">
          Usa ← → o desliza para navegar
        </p>
      </div>

      {/* CSS Animations */}
      <style>{`
        /* Page flip animations */
        @keyframes flipNextPage {
          0% { transform: rotateY(0deg); }
          100% { transform: rotateY(-180deg); }
        }
        
        @keyframes flipPrevPage {
          0% { transform: rotateY(0deg); }
          100% { transform: rotateY(180deg); }
        }
        
        .flip-next {
          animation: flipNextPage 0.35s ease-in-out forwards;
          transform-origin: left center;
        }
        
        .flip-prev {
          animation: flipPrevPage 0.35s ease-in-out forwards;
          transform-origin: right center;
        }
        
        .page-wrapper {
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
        }
        
        /* Holographic effect */
        @keyframes holoShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        .animate-holo {
          background-size: 200% 200%;
          animation: holoShift 3s ease-in-out infinite;
        }
        
        /* Shimmer for secret rares */
        @keyframes shimmer {
          0% { opacity: 0.3; }
          50% { opacity: 0.6; }
          100% { opacity: 0.3; }
        }
        
        .animate-shimmer {
          animation: shimmer 2s ease-in-out infinite;
        }
        
        /* Shadow fade */
        @keyframes fadeShadow {
          0% { opacity: 0; }
          50% { opacity: 1; }
          100% { opacity: 0; }
        }
        
        .animate-fade-shadow {
          animation: fadeShadow 0.35s ease-in-out;
        }
        
        /* Card slot hover lift */
        .card-slot:hover {
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4), 
                      0 0 15px rgba(250, 204, 21, 0.2);
        }
        
        /* Binder cover depth */
        .binder-cover {
          box-shadow: 
            0 25px 50px -12px rgba(0, 0, 0, 0.5),
            0 0 0 1px rgba(255, 255, 255, 0.1) inset,
            0 -2px 0 rgba(0, 0, 0, 0.3) inset;
        }
        
        /* Navigation button pulse */
        .nav-btn:not(:disabled):hover {
          animation: pulse 1s ease-in-out infinite;
        }
        
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(236, 72, 153, 0.4); }
          50% { box-shadow: 0 0 0 10px rgba(236, 72, 153, 0); }
        }
      `}</style>
    </div>
  );
};

export default CardBinder;
